import { Controller } from "@hotwired/stimulus";
import { connectPath } from "../base_path";
import { connectPixelToken } from "../pixel_token";

// Connect passwordless auth (SUR-2174 / SUR-2438 / SUR-2456). Initial
// registration verifies email once; returning login verifies the selected
// previously-verified channel once. Both
// forms keep a real action/method, so a no-JS first step still works. Requests
// are same-origin and carry the session cookie so verification can establish the
// signed-in Connect session.
export default class extends Controller {
  static targets = [
    "startForm", "identifier", "email", "phone", "name", "error",
    "codeForm", "codeIdentifier", "code", "codeError", "sentTo", "emailHint", "phoneHint",
    "stepUpNotice", "verifyButton",
    "profileForm", "profileName", "profilePhone", "profileEmail", "profileError",
  ];
  static values = { codeEndpoint: { type: String, default: "/auth/connect/code" } };

  // Email is the identifier step 2 verifies against. The registration request
  // carries both required fields so the resulting contact captures both.
  verifyIdentifier() {
    if (this.hasIdentifierTarget) return this.identifierTarget.value.trim();
    const email = this.hasEmailTarget ? this.emailTarget.value.trim() : "";
    const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : "";
    return email || phone;
  }

  async start(event) {
    event.preventDefault();
    this.clear(this.errorTarget);
    const id = this.verifyIdentifier();
    if (this.hasIdentifierTarget) {
      if (!id) {
        this.show(this.errorTarget, "Enter your email or mobile number.");
        return;
      }
    } else {
      const email = this.hasEmailTarget ? this.emailTarget.value.trim() : "";
      const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : "";
      if (!email || !phone) {
        this.show(this.errorTarget, "Enter both your email and mobile number.");
        return;
      }
      if (!this.emailTarget.checkValidity()) {
        this.show(this.errorTarget, "Enter a valid email address.");
        return;
      }
      // Mirror the server's minimum (10 digits after E.164 normalization) so a
      // phone it can't normalize is caught here, next to the field, instead of
      // costing a round trip and one of the visitor's rate-limit attempts.
      if (phone.replace(/\D/g, "").length < 10) {
        this.show(this.errorTarget, "Enter a valid mobile phone number.");
        return;
      }
    }

    this.disable(this.startFormTarget, true);
    try {
      const res = await fetch("/_register", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: this.withPixelToken(new FormData(this.startFormTarget)),
        credentials: "same-origin",
      });
      // A 422 (bad email/phone) or 429 (throttled) is a MESSAGE, not a broken
      // transport — the server already sent the exact copy to show. Re-submitting
      // natively here threw that message away and dumped the visitor back on the
      // page with no feedback at all (and, when throttled, burned another
      // request against the same cap). Only a genuine transport/parse failure
      // falls through to the no-JS native submit below.
      if (res.status === 422 || res.status === 429) {
        const data = await res.json().catch(() => ({}));
        this.show(this.errorTarget, this.startErrorMessage(res.status, data.error));
        return;
      }
      if (!res.ok) throw new Error(`register failed (${res.status})`);
      this.advanceToCode(id, this.hasIdentifierTarget ? "login" : "email");
    } catch (_e) {
      // No JSON path available — fall back to a native submit (server returns
      // the ?registration=sent page).
      this.startFormTarget.submit();
    } finally {
      this.disable(this.startFormTarget, false);
    }
  }

  // The throttle response is deliberately generic server-side ("rate_limited")
  // so it can't be used to enumerate addresses; turn it into something a
  // visitor can act on rather than showing them the raw token.
  startErrorMessage(status, serverError) {
    if (status === 429 || serverError === "rate_limited") {
      return "Too many attempts. Please wait a few minutes and try again.";
    }
    return serverError || "Enter a valid email address and phone number.";
  }

  advanceToCode(identifier, stage = "login") {
    this.verificationStage = stage;
    if (this.hasCodeIdentifierTarget) this.codeIdentifierTarget.value = identifier;
    if (this.hasSentToTarget) this.sentToTarget.textContent = identifier;
    // The "tap the link in your email" hint only applies to email.
    const isEmail = identifier.includes("@");
    if (this.hasEmailHintTarget) this.emailHintTarget.hidden = !isEmail;
    // Its counterpart for the other channel: a text only arrives if the visitor
    // registered with this number, and the uniform /_register response gives us
    // no way to tell. Shown to every phone submission (so it leaks nothing),
    // because the alternative is an unregistered visitor waiting indefinitely on
    // this screen — which is exactly how this reached us as a bug report.
    if (this.hasPhoneHintTarget) this.phoneHintTarget.hidden = isEmail;
    if (this.hasVerifyButtonTarget) this.verifyButtonTarget.textContent = "Verify & continue";
    this.startFormTarget.hidden = true;
    this.codeFormTarget.hidden = false;
    if (this.hasCodeTarget) this.codeTarget.focus();
  }

  // Return to step 1 so the visitor can fix a mistyped email/phone. The start
  // form still holds their previous entry, so they can edit and resubmit (which
  // issues a fresh code, superseding the old token).
  back(event) {
    event.preventDefault();
    this.steppingUp = false;
    if (this.hasStepUpNoticeTarget) this.stepUpNoticeTarget.hidden = true;
    this.clear(this.codeErrorTarget);
    if (this.hasCodeTarget) this.codeTarget.value = "";
    this.codeFormTarget.hidden = true;
    this.startFormTarget.hidden = false;
    const focusTarget = this.hasIdentifierTarget
      ? this.identifierTarget
      : this.hasEmailTarget
        ? this.emailTarget
        : this.hasPhoneTarget
          ? this.phoneTarget
          : null;
    if (focusTarget) focusTarget.focus();
  }

  async verify(event) {
    event.preventDefault();
    this.clear(this.codeErrorTarget);
    const code = this.hasCodeTarget ? this.codeTarget.value.trim() : "";
    if (!/^\d{6}$/.test(code)) {
      this.show(this.codeErrorTarget, "Enter the 6-digit code.");
      return;
    }

    this.disable(this.codeFormTarget, true);
    try {
      const res = await fetch(this.codeEndpointValue, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(this.codeFormTarget),
        credentials: "same-origin",
      });
      const data = await res.json().catch(() => ({}));
      // A phone sign-in on a number nothing ever proved is only half done: the
      // text showed they hold the handset, and now a code emailed to the address
      // on the account has to show it is their account. Stay on this step and
      // ask for the second code.
      if (res.ok && data.needs_email_confirmation) {
        this.advanceToEmailConfirmation(data.sent_to);
        return;
      }
      if (res.ok && data.redirect) {
        // A sign-in that turned into a registration arrives here with a verified
        // email and nothing else, so collect the rest before leaving the modal.
        if (data.needs_profile && this.hasProfileFormTarget) {
          this.pendingRedirect = data.redirect;
          this.advanceToProfile(data.profile_field);
          return;
        }
        this.navigate(this.redirectWithIntent(data.redirect));
        return;
      }
      this.show(this.codeErrorTarget, data.error || "That code is invalid or expired.");
      this.disable(this.codeFormTarget, false);
    } catch (_e) {
      this.show(this.codeErrorTarget, "Something went wrong — please try again.");
      this.disable(this.codeFormTarget, false);
    }
  }

  // Second leg of a phone sign-in. The server holds which address the follow-up
  // code went to, so the client only ever sees it masked and just posts the code.
  advanceToEmailConfirmation(sentTo) {
    this.steppingUp = true;
    this.clear(this.codeErrorTarget);
    if (this.hasCodeTarget) {
      this.codeTarget.value = "";
      this.codeTarget.focus();
    }
    if (this.hasSentToTarget) this.sentToTarget.textContent = sentTo || "the address on your account";
    // The text arrived; what's outstanding is the email. Both hints go away: the
    // phone one is answered, and the email one offers to "tap the link in your
    // email" — but the step-up mail is deliberately code-only, so at the most
    // security-sensitive screen in the flow that would send the visitor hunting
    // for a bearer link this design removed on purpose. stepUpNotice says what
    // is actually needed.
    if (this.hasPhoneHintTarget) this.phoneHintTarget.hidden = true;
    if (this.hasEmailHintTarget) this.emailHintTarget.hidden = true;
    if (this.hasStepUpNoticeTarget) this.stepUpNoticeTarget.hidden = false;
    if (this.hasVerifyButtonTarget) this.verifyButtonTarget.textContent = "Verify & continue";
    this.disable(this.codeFormTarget, false);
  }

  // Show the ONE field the server says is missing. Defaults to phone so an older
  // response carrying only needs_profile behaves exactly as it used to.
  advanceToProfile(field) {
    this.profileField = field === "email" ? "email" : "phone";
    const wantEmail = this.profileField === "email";
    this.codeFormTarget.hidden = true;
    this.profileFormTarget.hidden = false;
    // Disabled as well as hidden: a hidden-but-enabled input still rides along
    // in the FormData, and an empty one would draw a validation complaint about
    // a field the visitor was never shown.
    this.toggleProfileField(this.hasProfilePhoneTarget && this.profilePhoneTarget, !wantEmail);
    this.toggleProfileField(this.hasProfileEmailTarget && this.profileEmailTarget, wantEmail);
    const active = wantEmail
      ? this.hasProfileEmailTarget && this.profileEmailTarget
      : this.hasProfilePhoneTarget && this.profilePhoneTarget;
    if (active) active.focus();
  }

  toggleProfileField(input, show) {
    if (!input) return;
    input.hidden = !show;
    input.disabled = !show;
  }

  // Save the details a sign-in-turned-registration never asked for. The session
  // is already established, so "skip" is a legitimate exit rather than an
  // abandoned signup — it just leaves the agent a thinner contact.
  async saveProfile(event) {
    event.preventDefault();
    this.clear(this.profileErrorTarget);
    if (this.profileField === "email") {
      const email = this.hasProfileEmailTarget ? this.profileEmailTarget.value.trim() : "";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        this.show(this.profileErrorTarget, "Enter a valid email address.");
        return;
      }
    } else {
      const phone = this.hasProfilePhoneTarget ? this.profilePhoneTarget.value.trim() : "";
      if (phone.replace(/\D/g, "").length < 10) {
        this.show(this.profileErrorTarget, "Enter a valid mobile phone number.");
        return;
      }
    }

    this.disable(this.profileFormTarget, true);
    try {
      const res = await fetch("/auth/connect/profile", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(this.profileFormTarget),
        credentials: "same-origin",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        this.show(this.profileErrorTarget, data.error || "We couldn't save those details.");
        this.disable(this.profileFormTarget, false);
        return;
      }
      this.finishProfile();
    } catch (_e) {
      this.show(this.profileErrorTarget, "Something went wrong — please try again.");
      this.disable(this.profileFormTarget, false);
    }
  }

  skipProfile(event) {
    event.preventDefault();
    this.finishProfile();
  }

  finishProfile() {
    this.navigate(this.redirectWithIntent(this.pendingRedirect || "/"));
  }

  navigate(path) {
    // Server-composed redirects already carry the path-preview base; connectPath
    // is idempotent, so this only guards older/unprefixed values.
    window.location.assign(connectPath(path));
  }

  async resend(event) {
    event.preventDefault();
    try {
      // Re-POST the original step-1 form (still in the DOM, just hidden) so a
      // resent token keeps the name, return_to, and BOTH identifiers — the
      // newest token wins at verify. Falls back to the carried identifier alone
      // if the form target is somehow missing.
      let body;
      if (this.hasStartFormTarget) {
        body = new FormData(this.startFormTarget);
      } else {
        body = new FormData();
        body.append("identifier", this.hasCodeIdentifierTarget ? this.codeIdentifierTarget.value : "");
        if (this.hasNameTarget) body.append("name", this.nameTarget.value);
      }
      // Mid step-up the outstanding code is the EMAILED one. Reposting the start
      // form alone would text a fresh code and announce it, while the visitor is
      // still on the email step — so say which one to resend.
      if (this.steppingUp) body.set("step_up", "1");
      const res = await fetch("/_register", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: this.withPixelToken(body),
        credentials: "same-origin",
      });
      // "Sent a new code." used to be shown unconditionally, so a THROTTLED
      // resend — the state a visitor tapping "Resend code" is most likely to
      // reach — reported success while the server had deliberately sent
      // nothing. Reuse step 1's mapping so a 429/422 says what actually
      // happened. Anything else keeps the generic confirmation: /_register
      // answers 200 whether or not it sent, and narrowing that here would leak
      // which identifiers exist.
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // 422/429 carry copy the visitor can act on. Anything else is a
        // transport/proxy failure — /_register itself rescues to a 200 — and
        // must not be reported as a code that went out either.
        this.show(this.codeErrorTarget, res.status === 422 || res.status === 429
          ? this.startErrorMessage(res.status, data.error)
          : "Something went wrong — please try again.");
        return;
      }
      this.show(this.codeErrorTarget, "Sent a new code.");
    } catch (_e) {
      /* best-effort */
    }
  }

  // Attach this tab's Connect pixel token so the issued registration token
  // carries it (SUR-1883). Completion then identifies the visitor's anonymous
  // PixelSession and backfills the property views they browsed before signing
  // in. No-op in private mode (token null). Works for both the typed-code path
  // (same device) and the emailed magic link (the token persists it server-side).
  withPixelToken(formData) {
    const token = connectPixelToken();
    if (token) formData.set("pixel_token", token);
    return formData;
  }

  // Registration launched from a CMA consultation returns to the report and
  // reopens the consultation modal. The intent stays in the URL fragment (never
  // sent to the server), so it cannot broaden the controller's redirect target.
  redirectWithIntent(path) {
    const consultation = this.element.closest('[data-connect-contact-requires-registration-value="true"]');
    if (!consultation) return path;

    const url = new URL(path, window.location.origin);
    url.hash = "schedule-consultation";
    return `${url.pathname}${url.search}${url.hash}`;
  }

  disable(form, on) {
    const button = form.querySelector("button[type=submit]");
    if (button) button.disabled = on;
  }

  show(el, message) {
    if (!el) return;
    el.textContent = message;
    el.classList.add("is-visible");
  }

  clear(el) {
    if (!el) return;
    el.textContent = "";
    el.classList.remove("is-visible");
  }
}
